import React, { useContext } from 'react';
import { Person, Globe, ListStars, Speedometer2 } from 'react-bootstrap-icons';
import { useTheme } from '../../contexts/ThemeContext';
import { AuthContext } from '../../contexts/AuthContext';
import { TradingContext } from '../../contexts/TradingContext';
import PrecisionAIIcon from '../shared/PrecisionAIIcon';
import { canAccessTradeWorkspace } from '../../utils/brokerConnection';

/**
 * Floating island bottom navigation for mobile (iOS-style glass capsule) — 5 slots:
 * Watchlists | Market | Trade|AI (center logo) | AI|Performance | Portfolio
 *
 * When Trade is available, center is Trade and the 4th slot is PrecisionAI.
 * If Trade is hidden, center is PrecisionAI and the 4th slot is labeled Performance.
 * Account Health lives under Portfolio > Health.
 *
 * The center trade logo is intentionally oversized and overlaps content above the island.
 *
 * @param {string | null} activePage - Highlighted tab. Pass null on auxiliary routes
 *   so tapping Trade always navigates instead of retap-only.
 */
const BottomNavTray = ({ activePage, onPageChange }) => {
  const { colors, themeName } = useTheme();
  const { user } = useContext(AuthContext);
  const { selectedAccount } = useContext(TradingContext);
  const showTrade = canAccessTradeWorkspace(user, selectedAccount) !== false;
  const centerPage = showTrade ? 'trade' : 'askAI';
  const centerLabel = showTrade ? 'Trade' : 'AI';
  const analyticsPage = showTrade ? 'askAI' : 'performance';
  const analyticsLabel = showTrade ? 'AI' : 'Performance';
  const centerActive = activePage === centerPage;
  const analyticsActive = activePage === analyticsPage;
  const isDark = themeName !== 'light';

  const handleNavigate = (page) => {
    if (page === activePage) {
      try {
        window.dispatchEvent(new CustomEvent(`bottomnav:${page}:retap`));
      } catch (_) {}
      return;
    }
    onPageChange(page);
  };

  const navItemStyle = (isActive) => ({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: '6px 0 2px',
    minWidth: 0,
    cursor: 'pointer',
    transition: 'color 0.2s ease',
    color: isActive ? colors.primary : colors.textMuted,
    WebkitTapHighlightColor: 'transparent',
  });

  const logoContainerStyle = (isActive) => ({
    width: '58px',
    height: '58px',
    borderRadius: '50%',
    backgroundColor: isDark ? 'rgba(12, 15, 25, 0.92)' : 'rgba(255, 255, 255, 0.95)',
    border: `2px solid ${isActive ? colors.primary : colors.borderLight}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    boxShadow: isActive
      ? `0 4px 16px ${colors.primary}40, 0 2px 8px rgba(0,0,0,0.25)`
      : '0 4px 14px rgba(0,0,0,0.28)',
    marginTop: '-30px',
    flexShrink: 0,
  });

  const labelStyle = {
    fontSize: '10px',
    fontWeight: 500,
    marginTop: '4px',
    lineHeight: 1.1,
    whiteSpace: 'nowrap',
  };

  return (
    <nav
        className="mobile-bottom-nav"
        aria-label="Main"
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          display: 'flex',
          justifyContent: 'center',
          paddingLeft: 12,
          paddingRight: 12,
          paddingBottom: 'calc(10px + env(safe-area-inset-bottom, 0px))',
          pointerEvents: 'none',
          background: 'transparent',
        }}
      >
        <div
          data-testid="mobile-bottom-nav-island"
          style={{
            pointerEvents: 'auto',
            width: '100%',
            maxWidth: 440,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-around',
            padding: '4px 6px 8px',
            borderRadius: 28,
            // Darker grey frost: low fill so the chart reads through, strong blur so it stays muted
            backgroundColor: isDark
              ? 'rgba(18, 20, 24, 0.34)'
              : 'rgba(92, 96, 106, 0.28)',
            border: isDark
              ? '1px solid rgba(255, 255, 255, 0.16)'
              : '1px solid rgba(255, 255, 255, 0.42)',
            boxShadow: isDark
              ? [
                  '0 0 0 0.5px rgba(255, 255, 255, 0.08)',
                  '0 8px 28px rgba(0, 0, 0, 0.45)',
                  'inset 0 1px 0 rgba(255, 255, 255, 0.12)',
                  'inset 0 -1px 0 rgba(255, 255, 255, 0.03)',
                ].join(', ')
              : [
                  '0 0 0 0.5px rgba(255, 255, 255, 0.35)',
                  '0 8px 24px rgba(0, 0, 0, 0.12)',
                  'inset 0 1px 0 rgba(255, 255, 255, 0.45)',
                ].join(', '),
            backdropFilter: 'blur(48px) saturate(140%)',
            WebkitBackdropFilter: 'blur(48px) saturate(140%)',
            overflow: 'visible',
          }}
        >
          <div
            style={navItemStyle(activePage === 'watchlists')}
            onClick={() => handleNavigate('watchlists')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') handleNavigate('watchlists');
            }}
          >
            <ListStars size={22} />
            <span style={labelStyle}>Watchlists</span>
          </div>

          <div
            style={navItemStyle(activePage === 'market')}
            onClick={() => handleNavigate('market')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') handleNavigate('market');
            }}
          >
            <Globe size={21} />
            <span style={labelStyle}>Market</span>
          </div>

          <div
            style={navItemStyle(centerActive)}
            onClick={() => handleNavigate(centerPage)}
            role="button"
            tabIndex={0}
            aria-label={centerLabel}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') handleNavigate(centerPage);
            }}
          >
            <div style={logoContainerStyle(centerActive)}>
              <img
                src={`${process.env.PUBLIC_URL}/logo-transparent.png`}
                alt=""
                role="presentation"
                style={{ width: '64px', height: '64px', objectFit: 'contain' }}
              />
            </div>
            <span style={labelStyle}>{centerLabel}</span>
          </div>

          <div
            style={navItemStyle(analyticsActive)}
            onClick={() => handleNavigate(analyticsPage)}
            role="button"
            tabIndex={0}
            aria-label={analyticsLabel}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') handleNavigate(analyticsPage);
            }}
          >
            {analyticsPage === 'askAI' ? (
              <PrecisionAIIcon
                size={21}
                color={analyticsActive ? colors.primary : colors.textMuted}
              />
            ) : (
              <Speedometer2
                size={21}
                style={{
                  color: analyticsActive ? colors.primary : colors.textMuted,
                }}
              />
            )}
            <span style={labelStyle}>{analyticsLabel}</span>
          </div>

          <div
            style={navItemStyle(activePage === 'portfolio')}
            onClick={() => handleNavigate('portfolio')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') handleNavigate('portfolio');
            }}
          >
            <Person size={21} />
            <span style={labelStyle}>Portfolio</span>
          </div>
        </div>
      </nav>
  );
};

export default BottomNavTray;
